/**
 * ============================================================================
 * OBJECTS STORE - BLUEPRINT UTILITIES
 * ============================================================================
 * 
 * Utilities for checking blueprint usage across objects.
 * 
 * Use these functions to:
 * - Check if a blueprint is in use before deleting
 * - Find all objects using a specific blueprint
 * - Support blueprint migration workflows
 * 
 * @module objects/utils/blueprints
 */

import { objectsStore } from "../store";
import type { ObjectItem } from "../types";

// ============================================================================
// BLUEPRINT UTILITIES
// ============================================================================

/**
 * Get all objects that are using a specific blueprint.
 * 
 * Use this to:
 * - Find objects that need migration when a blueprint is updated
 * - Check dependencies before deleting a blueprint
 * - Display usage statistics
 * 
 * @param blueprintId - The blueprint ID to search for
 * @param gameId - The game ID for filtering
 * @returns Array of objects using this blueprint
 * 
 * @example
 * ```ts
 * const usersOfBlueprint = getObjectsUsingBlueprint('blueprint-123', gameId);
 * console.log(`${usersOfBlueprint.length} objects use this blueprint`);
 * ```
 */
export function getObjectsUsingBlueprint(blueprintId: string, gameId: string): ObjectItem[] {
  const allItems = objectsStore.getData();
  return allItems.filter(item =>
    item.game_id === gameId &&
    item.blueprint_id === blueprintId
  );
}

/**
 * @deprecated Use getObjectsUsingBlueprint instead
 */
export const getGlossaryItemsUsingBlueprint = getObjectsUsingBlueprint;

/**
 * Check if a blueprint is being used by any objects.
 * 
 * Use this for validation before deleting a blueprint:
 * - If true, warn the user or prevent deletion
 * - If false, safe to delete
 * 
 * @param blueprintId - The blueprint ID to check
 * @param gameId - The game ID for filtering
 * @returns True if the blueprint is being used
 * 
 * @example
 * ```ts
 * if (isBlueprintInUse(blueprintId, gameId)) {
 *   toast({ title: 'Cannot delete', description: 'Blueprint is in use' });
 *   return;
 * }
 * ```
 */
export function isBlueprintInUse(blueprintId: string, gameId: string): boolean {
  const itemsUsingBlueprint = getObjectsUsingBlueprint(blueprintId, gameId);
  return itemsUsingBlueprint.length > 0;
}
